import { useCallback, useEffect, useMemo } from 'react';

import find from 'lodash/find';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';

import { useSchedulePeriodsV2 } from '@sbv-clients/data-access-api';
import {
  Select,
  useTheme,
  useTransformSelectValue,
} from '@sbv-clients/ui-shared';
import { formatDateTimeString } from '@sbv-clients/util-misc';

const PeriodLabel = ({
  period: { startDate, endDate },
}: {
  period: { startDate?: string | null; endDate?: string | null };
}) => {
  const theme = useTheme();
  return (
    <>
      {formatDateTimeString(startDate, 'MM/dd/yyyy')}
      <span style={{ color: theme.text.placeholder, fontWeight: 'bold' }}>
        &nbsp;to&nbsp;
      </span>
      {formatDateTimeString(endDate, 'MM/dd/yyyy')}
    </>
  );
};

const useStartAndEndDate = ({
  facilityId,
  departmentId,
  schedulePeriodId,
}: {
  facilityId?: number;
  departmentId: number;
  schedulePeriodId: number;
}) => {
  const { data: schedulePeriods, isSuccess } = useSchedulePeriodsV2(
    departmentId,
    facilityId
  );
  return useMemo(() => {
    const schedulePeriodMap = keyBy(schedulePeriods, 'schedulePeriodId');
    const schedulePeriod = schedulePeriodMap[schedulePeriodId];
    return {
      startDate: schedulePeriod?.startDate || '',
      endDate: schedulePeriod?.endDate || '',
      isSuccess,
    };
  }, [isSuccess, schedulePeriodId, schedulePeriods]);
};

export function SchedulePeriodFilter({
  useContextFn,
  errorMessages = {},
}: {
  useContextFn: () => {
    facilityId?: number;
    departmentId: number;
    schedulePeriodId: number;
    setSchedulePeriodId: (value: number) => void;
  };
  errorMessages?: Record<string, string>;
}) {
  const {
    facilityId,
    departmentId,
    schedulePeriodId: value,
    setSchedulePeriodId,
  } = useContextFn();

  const {
    data: schedulePeriods,
    isSuccess,
    isLoading,
  } = useSchedulePeriodsV2(departmentId, facilityId);

  const options = useMemo(
    () =>
      map(schedulePeriods, (period) => ({
        id: period.schedulePeriodId as number,
        value: period.schedulePeriodId as number,
        label: <PeriodLabel period={period} />,
      })),
    [schedulePeriods]
  );

  const onChange = useCallback(setSchedulePeriodId, [setSchedulePeriodId]);

  // SPD-4981: Always sync to the API's isDefault period when schedule periods data loads.
  // Previously, the stored schedulePeriodId from localStorage would persist across sessions,
  // causing the filter to show an outdated period even after the period boundary had crossed.
  // Excluding `value` from deps ensures the user can still manually select a different period
  // within the same session without being overridden.
  useEffect(() => {
    if (!isSuccess) return;
    const schedulePeriodId =
      find(schedulePeriods, { isDefault: true })?.schedulePeriodId ?? 0;
    onChange(schedulePeriodId);
    // eslint-disable-next-line
  }, [isSuccess, schedulePeriods]);

  const handleChange = useTransformSelectValue(onChange);
  const helperText = errorMessages['schedulePeriodId'];
  return (
    <Select
      label="Schedule Period"
      value={value}
      onChange={handleChange}
      options={options}
      fullWidth
      size="small"
      error={!!helperText}
      helperText={helperText}
      isLoading={isLoading}
    />
  );
}

SchedulePeriodFilter.useStartAndEndDate = useStartAndEndDate;
